import { useState } from 'react';
import { X, CheckCircle2, Search, ImageOff, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

export const EventImportReview = ({ drafts, onCancel, onConfirm, saving }) => {
  const [items, setItems] = useState(drafts.map((d) => ({ ...d, _include: true })));

  const update = (idx, field, value) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };

  const included = items.filter((it) => it._include);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="import-review-modal">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-slate-700/70">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Sparkles className="w-4.5 h-4.5 text-purple-400" />
            </div>
            <div>
              <h3 className="text-white font-bold">Review AI Import</h3>
              <p className="text-xs text-slate-400">{items.length} events found — untick any you don't want, edit anything before adding</p>
            </div>
          </div>
          <button onClick={onCancel} className="text-slate-400 hover:text-white" data-testid="import-review-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {items.map((ev, idx) => (
            <div
              key={idx}
              className={cn(
                "border rounded-xl p-4 transition-opacity",
                ev._include ? "border-slate-600 bg-slate-800/60" : "border-slate-700/50 bg-slate-800/20 opacity-50"
              )}
              data-testid={`import-draft-${idx}`}
            >
              <div className="flex items-start gap-4">
                <input
                  type="checkbox"
                  checked={ev._include}
                  onChange={(e) => update(idx, '_include', e.target.checked)}
                  className="mt-1.5 w-4 h-4 accent-purple-500"
                  data-testid={`import-include-${idx}`}
                />
                {ev.image ? (
                  <div className="relative flex-shrink-0">
                    <img src={ev.image} alt="poster" className="w-20 h-20 rounded-lg object-cover border border-slate-600" />
                    <button
                      onClick={() => update(idx, 'image', '')}
                      className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center"
                      title="Remove poster"
                      data-testid={`import-remove-image-${idx}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-slate-700/50 border border-slate-600/50 flex items-center justify-center flex-shrink-0">
                    <ImageOff className="w-5 h-5 text-slate-500" />
                  </div>
                )}
                <div className="flex-1 space-y-2.5 min-w-0">
                  <div className="flex items-center gap-2">
                    <Input value={ev.title} onChange={(e) => update(idx, 'title', e.target.value)} className="h-8 bg-slate-700 border-slate-600 text-white font-medium" placeholder="Title" />
                    {ev.ticket_found ? (
                      <span className="flex items-center gap-1 text-[10px] font-semibold uppercase whitespace-nowrap text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded-full">
                        <CheckCircle2 className="w-3 h-3" /> Ticket page found
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-[10px] font-semibold uppercase whitespace-nowrap text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-1 rounded-full">
                        <Search className="w-3 h-3" /> Search link
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <Input value={ev.venue} onChange={(e) => update(idx, 'venue', e.target.value)} className="h-8 bg-slate-700 border-slate-600 text-white text-sm" placeholder="Venue" />
                    <Input value={ev.location} onChange={(e) => update(idx, 'location', e.target.value)} className="h-8 bg-slate-700 border-slate-600 text-white text-sm" placeholder="Town/City" />
                    <Input type="date" value={ev.date} onChange={(e) => update(idx, 'date', e.target.value)} className="h-8 bg-slate-700 border-slate-600 text-white text-sm" />
                    <Input value={ev.time} onChange={(e) => update(idx, 'time', e.target.value)} className="h-8 bg-slate-700 border-slate-600 text-white text-sm" placeholder="Time" />
                  </div>
                  <Input value={ev.ticket_url} onChange={(e) => update(idx, 'ticket_url', e.target.value)} className="h-8 bg-slate-700 border-slate-600 text-slate-300 text-xs font-mono" placeholder="Ticket link" />
                  <Textarea
                    value={ev.description}
                    onChange={(e) => update(idx, 'description', e.target.value)}
                    rows={2}
                    className="bg-slate-700 border-slate-600 text-slate-300 text-sm resize-none"
                    placeholder="Description (optional)"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between p-5 border-t border-slate-700/70">
          <Button variant="ghost" onClick={onCancel} className="text-slate-400 hover:text-white" data-testid="import-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(included.map(({ _include, ticket_found, ...ev }) => ev))}
            disabled={saving || included.length === 0}
            className="bg-purple-500 hover:bg-purple-600 text-white px-6"
            data-testid="import-confirm-btn"
          >
            {saving ? 'Adding...' : `Add ${included.length} Event${included.length === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>
    </div>
  );
};
